import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { api } from '../../api';

/**
 * On-Call rotation detail (FEAT-051 Phase 1). Read + delete. The rotation record arrives via
 * router state (from the list row) and is re-fetched by id for the freshest `currentOnCallName`.
 * In-place member/cadence editing is a Phase 1 follow-up; for now rotations are edited by
 * re-creating (or via the API). Deleting soft-removes the rotation from auto-assignment.
 */
const field = (label, value) => (
  value === undefined || value === null || value === '' ? null : (
    <Box sx={{ marginBottom: '10px' }}>
      <Typography sx={{ fontSize: '13px', color: '#666' }}>{label}</Typography>
      <Typography sx={{ fontSize: '15px' }}>{String(value)}</Typography>
    </Box>
  )
);

const CADENCE = { '1': 'Daily', '7': 'Weekly (7 days)', '14': 'Biweekly (14 days)' };

const OnCallSchedulePage = () => {
  const theme = useTheme();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(state || null);
  const [loading, setLoading] = useState(!state);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      const res = await api('get', { id }, 'oncallschedules');
      if (res.status === 200) {
        const fetched = res.data && res.data.data ? res.data.data : res.data;
        if (fetched && (fetched.id || Array.isArray(fetched))) {
          setData(Array.isArray(fetched) ? fetched[0] : fetched);
        }
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line
  }, [id]);

  const handleDelete = async () => {
    if (!data?.id) return;
    setDeleting(true);
    const res = await api('delete', { id: data.id }, 'oncallschedule');
    setDeleting(false);
    if (res.status === 200) navigate('/oncall');
  };

  if (loading) {
    return (
      <Box sx={{ padding: '40px', textAlign: 'center' }}><CircularProgress /></Box>
    );
  }
  if (!data) {
    return <Box sx={{ padding: '40px' }}><Typography>Rotation not found.</Typography></Box>;
  }

  const members = data.memberNames && typeof data.memberNames === 'object'
    ? Object.values(data.memberNames).join(', ')
    : (Array.isArray(data.members) ? data.members.join(', ') : '');

  return (
    <Box sx={{ padding: '30px' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: '20px' }}>
        {data.name || 'On-Call Rotation'}
      </Typography>
      <Box sx={{ border: `1px solid ${theme.palette.custom?.gray || '#ccc'}`, borderRadius: '4px', padding: '20px', maxWidth: '640px' }}>
        {field('On call now', data.currentOnCallName)}
        {field('Purpose', data.purpose)}
        {field('Responders (rotation order)', members)}
        {field('Cadence', CADENCE[String(data.rotationLengthDays)] || data.rotationLengthDays)}
        {field('Handoff time', data.handoffTime)}
        {field('Rotation start date', data.anchorDate)}
        {field('Timezone', data.timezone)}
        {field('Auto-assign new incidents', data.isDefaultAssignment === 'True' ? 'Yes' : 'No')}
        <Box sx={{ marginTop: '20px' }}>
          <Button variant="outlined" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete rotation'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default OnCallSchedulePage;
